import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiCalendar, FiFilter, FiAlertCircle, FiFileText } from 'react-icons/fi';
import { reportesService, clientesService } from '../../services';
import { exportToPdf, exportToCsv } from '../../utils/exportReport';
import Autocomplete from '../../components/UI/Autocomplete';
import './ReportesCommon.scss';

const ReportesCreditos = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general'); // general, por-cliente
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Filtros
  const [period, setPeriod] = useState('month'); // week, month, quarter, year
  const [status, setStatus] = useState('all'); // all, active, overdue, paid
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClient, setSelectedClient] = useState('');

  useEffect(() => {
    loadReportData();
  }, [activeTab, period, status, startDate, endDate, selectedClient]);

  // Función de búsqueda de clientes para Autocomplete
  const searchClientes = async (searchTerm) => {
    try {
      const response = await clientesService.getClientes({
        is_active: true,
        search: searchTerm,
        limit: 50
      });
      if (response.success) {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error al buscar clientes:', error);
      return [];
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {
        period,
        status: status !== 'all' ? status : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined
      };

      // Agregar filtro de cliente si está en el tab específico
      if (activeTab === 'por-cliente' && selectedClient) {
        params.client_id = selectedClient;
      }

      const response = await reportesService.getCreditos(params);

      if (response.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Error al cargar reporte de créditos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Columnas de la exportación (misma tabla de créditos de la pantalla)
  const getCreditosColumns = () => {
    const cols = [];
    if (activeTab === 'general') {
      cols.push({ label: 'Cliente', value: (r) => r.client_name || '' });
    }
    cols.push(
      { label: 'Factura', value: (r) => r.invoice_number || 'N/A' },
      { label: 'Límite de Crédito', value: (r) => formatCurrency(r.credit_limit) },
      { label: 'Crédito Utilizado', value: (r) => formatCurrency(r.credit_used) },
      { label: 'Crédito Disponible', value: (r) => formatCurrency(r.credit_available) },
      { label: 'Monto Pendiente', value: (r) => formatCurrency(r.amount_pending) },
      { label: 'Estado', value: (r) => getCreditStatus(r.status).label },
      { label: 'Fecha Vencimiento', value: (r) => formatDate(r.due_date) },
      { label: 'Días Vencidos', value: (r) => (r.days_overdue > 0 ? `${r.days_overdue} días` : '-') }
    );
    return cols;
  };

  // Exportación por datos (ventana limpia con la tabla completa), en lugar de
  // imprimir la pantalla (que salía cortada, "como una captura").
  const handleExportPDF = () => {
    const rows = reportData?.credits || [];
    if (!rows.length) { alert('No hay créditos para exportar'); return; }
    const tabLabel = tabs.find(t => t.id === activeTab)?.label || 'General';
    exportToPdf(
      `Reporte de Créditos - ${tabLabel}`,
      getCreditosColumns(),
      rows,
      `${rows.length} registros · ${new Date().toLocaleDateString('es-MX')}`
    );
  };

  const handleExportCSV = () => {
    const rows = reportData?.credits || [];
    if (!rows.length) { alert('No hay créditos para exportar'); return; }
    exportToCsv(getCreditosColumns(), rows, `creditos_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'por-cliente', label: 'Por Cliente' }
  ];

  const periodOptions = [
    { value: 'week', label: 'Semana Actual' },
    { value: 'month', label: 'Mes Actual' },
    { value: 'quarter', label: 'Trimestre Actual' },
    { value: 'year', label: 'Año Actual' }
  ];

  const statusOptions = [
    { value: 'all', label: 'Todos los Estados' },
    { value: 'active', label: 'Activos' },
    { value: 'overdue', label: 'Vencidos' },
    { value: 'paid', label: 'Pagados' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCreditStatus = (status) => {
    const statusMap = {
      'active': { label: 'Activo', class: 'success' },
      'overdue': { label: 'Vencido', class: 'overdue' },
      'paid': { label: 'Pagado', class: 'paid' },
      'pending': { label: 'Pendiente', class: 'pending' }
    };
    return statusMap[status] || { label: status, class: '' };
  };

  return (
    <div className="reportes-detail-page">
      {/* Header */}
      <div className="reportes-detail-header">
        <div className="header-top">
          <button className="btn-back" onClick={() => navigate('/system/reportes')}>
            <FiArrowLeft />
            <span>Regresar a Reportes</span>
          </button>
          <h1 className="reportes-detail-title">Reporte de Créditos</h1>
        </div>

        <div className="header-actions">
          <button className="btn-export" onClick={handleExportPDF}>
            <FiFileText />
            <span>PDF</span>
          </button>
          <button className="btn-export btn-export--secondary" onClick={handleExportCSV}>
            <FiDownload />
            <span>CSV</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="reportes-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="reportes-filters">
        <div className="filter-group">
          <label className="filter-label">
            <FiCalendar />
            <span>Período</span>
          </label>
          <select
            className="filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">
            <FiFilter />
            <span>Estado</span>
          </label>
          <select
            className="filter-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">
            <FiCalendar />
            <span>Fecha Desde</span>
          </label>
          <input
            type="date"
            className="filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">Fecha Hasta</label>
          <input
            type="date"
            className="filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {activeTab === 'por-cliente' && (
          <div className="filter-group">
            <label className="filter-label">
              <FiFilter />
              <span>Cliente</span>
            </label>
            <Autocomplete
              value={selectedClient}
              onChange={setSelectedClient}
              onSearch={searchClientes}
              getOptionLabel={(cliente) => cliente.company_name || cliente.full_name || `Cliente #${cliente.id}`}
              getOptionValue={(cliente) => cliente.id}
              placeholder="Buscar cliente..."
              noResultsText="No se encontraron clientes"
            />
          </div>
        )}
      </div>

      {/* A quién pertenecen las tarjetas: sin esta nota, en "Por Cliente"
          no era claro si los números eran globales o del cliente elegido. */}
      {activeTab === 'por-cliente' && (
        <div className="summary-scope-note">
          {selectedClient
            ? `Los totales de abajo corresponden al cliente: ${
                reportData?.client_analysis?.[0]?.client_name
                || reportData?.credits?.[0]?.client_name
                || 'seleccionado'
              }`
            : 'Totales generales — selecciona un cliente para ver sus números'}
        </div>
      )}

      {/* Resumen */}
      {reportData?.summary && (
        <div className="reportes-summary">
          <div className="summary-card highlight">
            <span className="summary-label">Total Créditos Otorgados</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_credit_granted)}</span>
          </div>
          <div className="summary-card success">
            <span className="summary-label">Crédito Disponible</span>
            <span className="summary-value">{formatCurrency(reportData.summary.credit_available)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Crédito Utilizado</span>
            <span className="summary-value">{formatCurrency(reportData.summary.credit_used)}</span>
          </div>
          <div className="summary-card discount">
            <span className="summary-label">
              <FiAlertCircle style={{ display: 'inline', marginRight: '6px' }} />
              Créditos Vencidos
            </span>
            <span className="summary-value">{formatCurrency(reportData.summary.overdue_amount)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Clientes con Crédito</span>
            <span className="summary-value">{reportData.summary.clients_with_credit || 0}</span>
          </div>
        </div>
      )}

      {/* Tabla de Créditos */}
      <div className="reportes-table-container">
        {loading ? (
          <div className="loading-state">Cargando reporte...</div>
        ) : reportData?.credits && reportData.credits.length > 0 ? (
          <table className="reportes-table">
            <thead>
              <tr>
                {activeTab === 'general' && <th>Cliente</th>}
                <th>Factura</th>
                <th className="text-right">Límite de Crédito</th>
                <th className="text-right">Crédito Utilizado</th>
                <th className="text-right">Crédito Disponible</th>
                <th className="text-right">Monto Pendiente</th>
                <th className="text-center">Estado</th>
                <th className="text-right">Fecha Vencimiento</th>
                <th className="text-right">Días Vencidos</th>
              </tr>
            </thead>
            <tbody>
              {reportData.credits.map((credit, index) => {
                const statusInfo = getCreditStatus(credit.status);
                const isOverdue = credit.days_overdue > 0;

                return (
                  <tr key={index} className={isOverdue ? 'row-overdue' : ''}>
                    {activeTab === 'general' && (
                      <td className="font-bold">{credit.client_name}</td>
                    )}
                    <td>
                      <span className="invoice-badge">{credit.invoice_number || 'N/A'}</span>
                    </td>
                    <td className="text-right">{formatCurrency(credit.credit_limit)}</td>
                    <td className="text-right">{formatCurrency(credit.credit_used)}</td>
                    <td className="text-right success">{formatCurrency(credit.credit_available)}</td>
                    <td className="text-right font-bold">{formatCurrency(credit.amount_pending)}</td>
                    <td className="text-center">
                      <span className={`credit-status credit-status--${statusInfo.class}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="text-right">{formatDate(credit.due_date)}</td>
                    <td className="text-right">
                      {isOverdue ? (
                        <span className="overdue-days">{credit.days_overdue} días</span>
                      ) : (
                        <span className="normal-days">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay créditos registrados para los filtros seleccionados.</p>
          </div>
        )}
      </div>

      {/* Análisis por Cliente (solo en vista por-cliente) */}
      {activeTab === 'por-cliente' && reportData?.client_analysis && (
        <div className="reportes-table-container" style={{ marginTop: '30px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#4a3470',
            marginBottom: '15px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Análisis Detallado por Cliente
          </h3>
          <table className="reportes-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th className="text-right">Créditos Activos</th>
                <th className="text-right">Límite Total</th>
                <th className="text-right">Utilizado</th>
                <th className="text-right">Disponible</th>
                <th className="text-right">% Utilización</th>
                <th className="text-right">Días Promedio Pago</th>
              </tr>
            </thead>
            <tbody>
              {reportData.client_analysis.map((client, index) => (
                <tr key={index}>
                  <td className="font-bold">{client.client_name}</td>
                  <td className="text-right">{client.active_credits}</td>
                  <td className="text-right">{formatCurrency(client.total_limit)}</td>
                  <td className="text-right">{formatCurrency(client.total_used)}</td>
                  <td className="text-right success">{formatCurrency(client.total_available)}</td>
                  <td className="text-right">{formatPercentage(client.utilization_rate)}</td>
                  <td className="text-right">{client.avg_payment_days || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportesCreditos;
